import React, { useEffect, useMemo, useState } from "react";
import StakingService from "../../../api/services/StakingService";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import RedeemConfirmOverview from "./RedeemConfirmOverview";
import RedeemSuccessPopup from "./RedeemSuccessPopup";
import {
  formatStakeBalance,
  getPackageIcon,
  getTotalInvestedAmount,
  getUnbondingPeriodDays,
  resolvePackageFromPosition,
} from "./stakingStakeHelpers";
import "./staking.css";

const formatRedeemDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
};

const getTimeReceived = (unbondingDays = 0) => {
  const date = new Date();
  if (Number(unbondingDays) > 0) {
    date.setDate(date.getDate() + Number(unbondingDays));
  }
  return formatRedeemDateTime(date);
};

const getRedeemAmountError = (amount, availableAmount, currency, minAmount = 1) => {
  const num = Number(amount);
  if (!amount || !Number.isFinite(num) || num <= 0) return null;
  if (num < minAmount) {
    return `Minimum redemption amount is ${minAmount} ${currency}`;
  }
  if (num > availableAmount) {
    return `Insufficient balance. Available: ${formatStakeBalance(availableAmount)} ${currency}`;
  }
  return null;
};

const RedeemModal = ({ position, onClose, onSuccess }) => {
  const selectedPackage = useMemo(() => resolvePackageFromPosition(position), [position]);
  const pkg = position?.packageId || {};
  const currency = position?.currency || pkg?.currency || selectedPackage.currency || "";
  const availableAmount = parseFloat(getTotalInvestedAmount(position)) || 0;
  const minAmount = Number(pkg?.minAmount) > 0 ? Number(pkg.minAmount) : 1;
  const unbondingDays = getUnbondingPeriodDays(position);
  const timeRedeemed = "Now";
  const timeReceived = getTimeReceived(unbondingDays);

  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState("");
  const [showOverviewConfirm, setShowOverviewConfirm] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    setRedeemAmount("");
    setRedeemError("");
    setShowOverviewConfirm(false);
    setShowSuccessPopup(false);
  }, [position]);

  const validationError = useMemo(
    () => getRedeemAmountError(redeemAmount, availableAmount, currency, minAmount),
    [redeemAmount, availableAmount, currency, minAmount]
  );

  const handleMax = () => {
    setRedeemAmount(availableAmount > 0 ? String(availableAmount) : "");
  };

  const submitRedeem = async () => {
    setRedeemLoading(true);
    try {
      const res = await StakingService.redeem(String(position._id), Number(redeemAmount));
      if (res?.success) {
        setShowOverviewConfirm(false);
        setShowSuccessPopup(true);
      } else {
        const message = res?.message || "Redeem failed. Please try again.";
        alertErrorMessage(message);
        setShowOverviewConfirm(false);
      }
    } catch {
      alertErrorMessage("Something went wrong. Please try again.");
      setShowOverviewConfirm(false);
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleConfirm = () => {
    setRedeemError("");
    const error = getRedeemAmountError(redeemAmount, availableAmount, currency, minAmount);
    if (error) {
      setRedeemError(error);
      return;
    }
    setShowOverviewConfirm(true);
  };

  const handleOverviewBack = () => {
    if (redeemLoading) return;
    setShowOverviewConfirm(false);
  };

  const handleSuccessDone = () => {
    setShowSuccessPopup(false);
    onSuccess?.();
    onClose();
  };

  if (!position) return null;

  return (
    <>
      <div className="mint-modal-overlay" onClick={onClose}>
        <div className="mint-modal-content redeem-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="mint-modal-header">
            <div className="mint-modal-title">
              <img
                src={getPackageIcon({ ...selectedPackage, currency })}
                alt={currency}
                className="mint-title-icon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
                }}
              />
              <span>Redeem {currency}</span>
            </div>
            <button type="button" className="mint-modal-close" onClick={onClose}>
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="redeem-modal-body">
            <div className="mint-amount-section">
              <label className="mint-section-label">Amount</label>
              <div className="mint-input-box">
                <input
                  type="number"
                  placeholder={`Min. ${minAmount}`}
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(e.target.value)}
                />
                <div className="mint-input-addon">
                  <span className="mint-input-currency">{currency}</span>
                  <div className="mint-input-separator" />
                  <span className="mint-max-btn" onClick={handleMax}>Max</span>
                </div>
              </div>
              {(validationError || redeemError) && (
                <div className="mint-error-msg">
                  {validationError || redeemError}
                </div>
              )}
              <div className="mint-amount-details">
                <div className="mint-amount-row">
                  <span className="mint-amount-lbl">Available</span>
                  <span className="mint-amount-val">
                    {formatStakeBalance(availableAmount)} {currency}
                  </span>
                </div>
              </div>
            </div>

            <div className="mint-timeline redeem-modal-timeline">
              <div className="mint-timeline-item">
                <div className="mint-timeline-dot active" />
                <div className="mint-timeline-content">
                  <span className="mint-timeline-lbl">Time Redeemed</span>
                  <span className="mint-timeline-val">{timeRedeemed}</span>
                </div>
              </div>
              <div className="mint-timeline-item last">
                <div className="mint-timeline-dot" />
                <div className="mint-timeline-content">
                  <span className="mint-timeline-lbl">Time Received</span>
                  <span className="mint-timeline-val">{timeReceived}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mint-modal-footer redeem-modal-footer">
            <div className="mint-actions">
              <button type="button" className="mint-btn-cancel" disabled={redeemLoading} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="mint-btn-confirm"
                disabled={redeemLoading || !redeemAmount || !!validationError}
                onClick={handleConfirm}
              >
                Redemption
              </button>
            </div>
          </div>
        </div>
      </div>

      {showOverviewConfirm && (
        <RedeemConfirmOverview
          selectedPackage={selectedPackage}
          redeemAmount={redeemAmount}
          availableAmount={availableAmount}
          currency={currency}
          timeRedeemed={timeRedeemed}
          timeReceived={timeReceived}
          loading={redeemLoading}
          onBack={handleOverviewBack}
          onConfirm={submitRedeem}
        />
      )}

      {showSuccessPopup && (
        <RedeemSuccessPopup
          selectedPackage={selectedPackage}
          redeemAmount={redeemAmount}
          currency={currency}
          timeReceived={timeReceived}
          onDone={handleSuccessDone}
        />
      )}
    </>
  );
};

export default RedeemModal;
